import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Capacitor } from "@capacitor/core";
import {
  establishBackendSession,
  clearBackendSession,
  hasBackendSession,
  isUnauthenticatedError,
} from "@/auth/session";

// After this many consecutive auth failures we stop auto-logging-out and show
// a clear error instead. This is the safety valve that makes an infinite
// login <-> logout loop impossible even when Auth0/backend are misconfigured.
const MAX_AUTH_RETRIES = 1;
const FAILURE_KEY = "auth_establish_failures";

const readFailures = () => Number(sessionStorage.getItem(FAILURE_KEY) || "0");
const writeFailures = (n) => sessionStorage.setItem(FAILURE_KEY, String(n));

const Spinner = () => (
  <div className="dashboard-container" style={centerStyle}>
    <div className="loading-spinner" />
  </div>
);

const centerStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  textAlign: "center",
  padding: "1rem",
};

/**
 * Single authentication gate for all Auth0-protected pages. It guarantees a
 * backend session exists before rendering children, so pages no longer each
 * run their own token exchange (which used to hammer Auth0 /userinfo and
 * bounce the user between Landing and Dashboard).
 */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently, logout } = useAuth0();
  const [phase, setPhase] = useState("establishing"); // establishing | ready | transient | fatal
  const [attempt, setAttempt] = useState(0);
  const cancelled = useRef(false);

  const logoutClean = useCallback(() => {
    clearBackendSession();
    if (Capacitor.isNativePlatform()) {
      localStorage.clear();
      logout({ logoutParams: { returnTo: "be.rmworx.cilantrosafe://callback" }, openUrl: false });
    } else {
      logout({ logoutParams: { returnTo: window.location.origin } });
    }
  }, [logout]);

  useEffect(() => {
    cancelled.current = false;
    if (isLoading || !isAuthenticated) return undefined;

    // A session token from a previous visit is trusted; if it turns out to be
    // stale a protected call 401s, the interceptor clears it, and the next
    // navigation lands here without a token and re-establishes.
    if (hasBackendSession()) {
      writeFailures(0);
      setPhase("ready");
      return undefined;
    }

    const onError = (error) => {
      if (cancelled.current) return;
      if (!isUnauthenticatedError(error)) {
        setPhase("transient");
        return;
      }
      const failures = readFailures() + 1;
      writeFailures(failures);
      if (failures > MAX_AUTH_RETRIES) {
        setPhase("fatal"); // stop the loop; the user must re-login themselves
        return;
      }
      logoutClean();
    };

    setPhase("establishing");
    establishBackendSession(getAccessTokenSilently)
      .then(() => {
        if (cancelled.current) return;
        writeFailures(0);
        setPhase("ready");
      })
      .catch(onError);

    return () => {
      cancelled.current = true;
    };
  }, [isAuthenticated, isLoading, getAccessTokenSilently, logoutClean, attempt]);

  if (isLoading || phase === "establishing") return <Spinner />;
  if (!isAuthenticated) return <Navigate to="/" replace />;

  if (phase === "transient") {
    return (
      <div style={centerStyle}>
        <p>We couldn't reach the server. Please try again.</p>
        <button className="cta-button" onClick={() => setAttempt((a) => a + 1)}>
          Retry
        </button>
      </div>
    );
  }

  if (phase === "fatal") {
    return (
      <div style={centerStyle}>
        <p>We couldn't sign you in. Please sign in again.</p>
        <button
          className="cta-button"
          onClick={() => {
            writeFailures(0);
            logoutClean();
          }}
        >
          Back to sign in
        </button>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
